import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from './ui/button';
import { MapPin, Navigation, X } from 'lucide-react';

// Fix for default marker icon issue in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icon
const customIcon = new L.Icon({
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

// Component to handle map click events
function LocationMarker({ position, setPosition }) {
    useMapEvents({
        click(e) {
            setPosition([e.latlng.lat, e.latlng.lng]);
        },
    });

    return position ? <Marker position={position} icon={customIcon} /> : null;
}

// Component to recenter map
function MapController({ center }) {
    const map = useMap();
    
    useEffect(() => {
        if (center) {
            map.setView(center, 15);
        }
    }, [center, map]);
    
    return null;
}

export const LocationPicker = ({ 
    onLocationSelect, 
    initialPosition = null,
    initialLocationName = '',
    className = ''
}) => {
    const [position, setPosition] = useState(initialPosition);
    const [locationName, setLocationName] = useState(initialLocationName);
    const [isLocating, setIsLocating] = useState(false);
    const [showMap, setShowMap] = useState(false);
    const [mapCenter, setMapCenter] = useState([20.5937, 78.9629]); // Default: India center
    
    // Get current location
    const getCurrentLocation = () => {
        if (!navigator.geolocation) {
            alert('Geolocation is not supported by your browser');
            return;
        }
        
        setIsLocating(true);
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                const lat = pos.coords.latitude;
                const lng = pos.coords.longitude;
                setPosition([lat, lng]);
                setMapCenter([lat, lng]);
                setShowMap(true);
                
                // Reverse geocode to get location name
                try {
                    const response = await fetch(
                        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
                    );
                    const data = await response.json();
                    const name = data.display_name?.split(',').slice(0, 3).join(', ') || `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
                    setLocationName(name);
                    onLocationSelect({ latitude: lat, longitude: lng, locationName: name });
                } catch (error) {
                    const name = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
                    setLocationName(name);
                    onLocationSelect({ latitude: lat, longitude: lng, locationName: name });
                }
                
                setIsLocating(false);
            },
            (error) => {
                console.error('Geolocation error:', error);
                alert('Unable to get your location. Please select manually on the map.');
                setShowMap(true);
                setIsLocating(false);
            },
            { enableHighAccuracy: true, timeout: 10000 }
        );
    };
    
    // Handle position change from map click
    useEffect(() => {
        if (position && showMap) {
            const fetchLocationName = async () => {
                try {
                    const response = await fetch(
                        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${position[0]}&lon=${position[1]}`
                    );
                    const data = await response.json();
                    const name = data.display_name?.split(',').slice(0, 3).join(', ') || `${position[0].toFixed(4)}, ${position[1].toFixed(4)}`;
                    setLocationName(name);
                    onLocationSelect({ latitude: position[0], longitude: position[1], locationName: name });
                } catch (error) {
                    const name = `${position[0].toFixed(4)}, ${position[1].toFixed(4)}`;
                    setLocationName(name);
                    onLocationSelect({ latitude: position[0], longitude: position[1], locationName: name });
                }
            };
            fetchLocationName();
        }
    }, [position, showMap, onLocationSelect]);
    
    const clearLocation = () => {
        setPosition(null);
        setLocationName('');
        onLocationSelect({ latitude: null, longitude: null, locationName: '' });
    };
    
    return (
        <div className={`space-y-3 ${className}`}>
            {/* Location Display */}
            {locationName && (
                <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg border">
                    <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm flex-1 truncate">{locationName}</span>
                    <Button 
                        type="button" 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6"
                        onClick={clearLocation}
                    >
                        <X className="w-3 h-3" />
                    </Button>
                </div>
            )}
            
            {/* Action Buttons */}
            <div className="flex gap-2">
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={getCurrentLocation}
                    disabled={isLocating}
                    className="flex-1"
                    data-testid="get-current-location-btn"
                >
                    <Navigation className={`w-4 h-4 mr-2 ${isLocating ? 'animate-pulse' : ''}`} />
                    {isLocating ? 'Getting location...' : 'Use Current Location'}
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowMap(!showMap)}
                    data-testid="toggle-map-btn"
                >
                    <MapPin className="w-4 h-4 mr-2" />
                    {showMap ? 'Hide Map' : 'Pick on Map'}
                </Button>
            </div>
            
            {/* Map */}
            {showMap && (
                <div className="h-64 rounded-lg overflow-hidden border" data-testid="location-map">
                    <MapContainer
                        center={mapCenter}
                        zoom={position ? 15 : 5}
                        style={{ height: '100%', width: '100%' }}
                        scrollWheelZoom={true}
                    >
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <LocationMarker position={position} setPosition={setPosition} />
                        <MapController center={position || mapCenter} />
                    </MapContainer>
                    <p className="text-xs text-muted-foreground text-center mt-1">
                        Click on the map to select location
                    </p>
                </div>
            )}
        </div>
    );
};

// Read-only map for displaying violation location
export const LocationDisplay = ({ latitude, longitude, locationName, className = '' }) => {
    if (!latitude || !longitude) {
        return null;
    }
    
    const position = [latitude, longitude];
    
    return (
        <div className={`space-y-2 ${className}`}>
            <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-primary" />
                <span className="text-sm">{locationName || `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`}</span>
            </div>
            <div className="h-48 rounded-lg overflow-hidden border" data-testid="location-display-map">
                <MapContainer
                    center={position}
                    zoom={15}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom={false}
                    dragging={false}
                    zoomControl={false}
                >
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker position={position} icon={customIcon} />
                </MapContainer>
            </div>
        </div>
    );
};
